import React, { FC, useState, useEffect } from "react";
import { Table, Row, Col, Button, Input, Tag, Select } from "antd";

import { PlusCircleOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import { PagedResult } from "states/paged-result-model";
import { useDebouncedCallback } from "use-debounce/lib";

import momentTz from "moment-timezone";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "app.store";
import { RemovedEntityModel } from "shared/models/removed-entity-model";
import { searchPromotionCode } from "states/promotion-code/promotion-code.api";
import { Link } from "react-router-dom";
import {
  ADD_PROMOTION_CODE_ROUTE,
  UPDATE_PROMOTION_CODE_ROUTE,
} from "shared/constants/routes";
import { selectCurrentBranch } from "states/branch/branch.slice";
import { momentDateTimeFormat } from "shared/plugins/localization";
import {
  PromotionCodeModel,
  SearchPromotionCodeRequestModel,
} from "states/promotion-code/promotion-code.models";
import { PromotionConst } from "shared/models/promotion.model";

interface IPromotionCodeProps {}
const PromotionCode: FC<IPromotionCodeProps> = () => {
  const currentBranch = useSelector(selectCurrentBranch, shallowEqual);
  const currentTimeZone = useSelector(
    (state: RootState) => state.globalValue.ianaTimeZone,
    shallowEqual
  );
  const [selectedRowsKeys, setSelectedRowsKeys] = useState<
    RemovedEntityModel<string>
  >({
    idList: [],
  });
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [pagedResults, setPagedResults] = useState(
    {} as PagedResult<PromotionCodeModel>
  );

  const [filterModel, setFilterModel] =
    useState<SearchPromotionCodeRequestModel>({
      page: 1,
      pageSize: 20,
    });

  const rowSelection = {
    selectedRowsKeys,
    onChange: (selectedRowKeys: any) => {
      setSelectedRowsKeys({ ...selectedRowsKeys, idList: selectedRowKeys });
    },
  };
  const onEmptySelection = () => {
    setSelectedRowsKeys({ ...selectedRowsKeys, idList: [] });
  };
  const gotoPage = async (page: number, pageSize:number) => {
    setIsLoadingList(true);
    const result = await searchPromotionCode({ ...filterModel, page,pageSize });
    setPagedResults(result.data);
    setIsLoadingList(false);
  };
  const handleSearch = useDebouncedCallback(() => {
    gotoPage(1,20);
  }, 500);

  const expandedRowRender = (data: PromotionCodeModel) => {
    return (
      <div>
        <div></div>
      </div>
    );
  };
  const handleDate = (model: PromotionCodeModel, type: string) => {
    let date = model.promotion.conditions.find((m) => m.type === type);
    if (date === undefined) {
      return "";
    }
    return momentTz(date.dateAndTime)
      .tz(currentTimeZone)
      .format(momentDateTimeFormat());
  };
  const columns: ColumnsType<PromotionCodeModel> = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (code: string, item) => (
        <React.Fragment>
          <Link to={`${UPDATE_PROMOTION_CODE_ROUTE}/${item.id}`}>
            <strong>{code}</strong>
          </Link>
        </React.Fragment>
      ),
    },
    {
      title: "Ngày áp dụng",
      dataIndex: "date",
      key: "date",
      render: (date: string, item) => (
        <React.Fragment>
          <div>
            <p>
              Từ ngày:{" "}
              {handleDate(
                item,
                PromotionConst.promotionConditionType.startDate
              )}
            </p>
            <p>
              Đến ngày:{" "}
              {handleDate(item, PromotionConst.promotionConditionType.endDate)}
            </p>
          </div>
        </React.Fragment>
      ),
    },
    {
      title: "Trạng thái",
      dataIndex: "active",
      key: "active",
      render: (active: boolean, item) => (
        <React.Fragment>
          {active ? (
            <Tag color="#87d068">Hoạt động</Tag>
          ) : (
            <Tag color="#f50">Ngưng hoạt động</Tag>
          )}
        </React.Fragment>
      ),
    },
  ];

  useEffect(() => {
    handleSearch();
  }, [filterModel, currentBranch]);

  useEffect(() => {
    document.title = "Mã khuyến mãi";
  }, []);

  return (
    <div className="PromotionCode">
      <Row gutter={[16, 16]} justify="space-between" className="mb-4">
        <Col span={6}>
          <Input.Search
            value={filterModel.code}
            placeholder="Nhập mã khuyến mãi..."
            onChange={(e) => {
              setFilterModel({ ...filterModel, code: e.target.value });
            }}
          ></Input.Search>
        </Col>
        <Col span={6}>
          <Select
            className="w-100"
            placeholder="Chọn trạng thái"
            allowClear
            showSearch
            filterOption={(input, option: any) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            onChange={(value: number) => {
              setFilterModel({
                ...filterModel,
                active: value === 0 ? false : true,
              });
            }}
          >
            <Select.Option value={0}>
              <Tag color="#f50">Ngưng hoạt động</Tag>
            </Select.Option>
            <Select.Option value={1}>
              <Tag color="#87d068">Hoạt động</Tag>
            </Select.Option>
          </Select>
        </Col>
        <Col span={12} className="d-flex justify-content-end">
          <Link to={ADD_PROMOTION_CODE_ROUTE}>
            <Button type="primary" icon={<PlusCircleOutlined />}>
              Thêm mã
            </Button>
          </Link>
        </Col>
      </Row>
      <Table
        expandable={{
          expandedRowRender: (record) => expandedRowRender(record),
        }}
        rowSelection={rowSelection}
        columns={columns}
        dataSource={pagedResults.results}
        rowKey={(m) => m.id}
        size="small"
        pagination={{
          total: pagedResults.totalItemCount,
          pageSize: pagedResults.pageSize,
          current: pagedResults.currentPage,
          onChange: gotoPage,
        }}
        loading={isLoadingList}
      />
    </div>
  );
};

export default PromotionCode;
